/**
 * Tests Odoo connectivity from the browser before the credentials are sent
 * to Firestore for full backend validation.
 *
 * Why only a reachability check?
 *   Odoo Online does not enable CORS on /jsonrpc or /web/session/authenticate,
 *   so we cannot perform the real authentication directly from the browser
 *   (like we do for TraceX). Instead, we:
 *     1. Ping the user-provided base URL to confirm the instance responds.
 *     2. Let the backend (`trigger_odoo_auth`) do the real login validation
 *        against the database / login / password / api_key before persisting
 *        the credentials in Secret Manager.
 *
 * This mirrors the Qualishare flow (front: reachability, backend: real auth).
 */

export class OdooTestError extends Error {
  constructor(
    public readonly code: OdooTestErrorCode,
    public readonly i18nParams?: Record<string, string | number>,
  ) {
    super(code)
    this.name = 'OdooTestError'
  }
}

export type OdooTestErrorCode =
  | 'URL_REQUIRED'
  | 'URL_INVALID'
  | 'INSTANCE_UNREACHABLE'

/**
 * Extracts the Odoo base URL from any string the user pastes.
 *
 * Handles:
 *   - Full app URL  https://mycompany.odoo.com/web#action=123&view_type=list
 *   - Deep path     https://mycompany.odoo.com/odoo/documents/1234
 *   - Login page    https://mycompany.odoo.com/web/login
 *   - Root          https://mycompany.odoo.com/
 *   - No scheme     mycompany.odoo.com
 *   - With port     https://odoo.internal.corp:8069/web
 *
 * Always returns ``scheme://host[:port]`` — no path, no fragment, no query.
 * Returns ``""`` if the input cannot be parsed as a URL.
 */
export function normalizeOdooBaseUrl(input: string): string {
  const raw = (input || '').trim()
  if (!raw) return ''
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return ''
    // Keep only scheme + host (+ port if non-standard)
    return `${parsed.protocol}//${parsed.host}`
  } catch {
    return ''
  }
}

/**
 * Front-side reachability check.
 *
 * Tries to fetch ``{base}/web/version`` (public endpoint on Odoo 14+) to
 * confirm the host resolves and responds. CORS may block reading the body,
 * but a non-network failure means the instance exists.
 *
 * The real credential validation (database / login / password / api_key)
 * happens server-side via ``trigger_odoo_auth``.
 */
export async function testOdooConnection(opts: { url: string }): Promise<void> {
  const raw = (opts.url || '').trim()
  if (!raw) throw new OdooTestError('URL_REQUIRED')

  const base = normalizeOdooBaseUrl(raw)
  if (!base) throw new OdooTestError('URL_INVALID', { url: raw })

  try {
    // ``no-cors`` returns an opaque response (status=0) but does not throw
    // on CORS issues — only on real network/DNS errors. That's exactly what
    // we need: confirm the host exists without depending on CORS headers.
    await fetch(`${base}/web/version`, { method: 'GET', mode: 'no-cors' })
  } catch {
    throw new OdooTestError('INSTANCE_UNREACHABLE', { url: base })
  }
}
